import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { PropertyCategory } from '~/__generated__/__types__'
import { theme } from '~/app/components/styled'
import { getCancelPolicyTextInfo } from '~/app/utils/cancelPolicy'
import { OrderCancelPolicyDomestic } from '~/app/components/order-cancel/hooks/useOrderCancelPolicy'
import CancelPoliciesTooltip from '~/app/components/order-cancel/CancelPoliciesTooltip'
import ExceptionalFreeBottomSheet from '~/app/components/common/CancelPolicy/ExceptionalFreeBottomSheet'
import { Icon_arrowforward_fill, Icon_error, Icon_help } from '@yanolja-rnd/ya-com-system-icon'
import { Typography } from '@yanolja-rnd/ya-com-core'
import {
  CancelPolicyType,
  ICancelPolicy,
} from '~/api/converters/domestic/order/ProductSection/Property/index.type'
import useModalHash from '../useModalHash'

interface CancelPolicyProps {
  cancelPolicySummary: ICancelPolicySummary
  setIsCancelFree?: Dispatch<SetStateAction<boolean>>
  propertyCategory?: PropertyCategory
  isPendingReservation?: boolean
}

export interface ICancelPolicySummary {
  cancelPolicy?: ICancelPolicy
  cancelable?: boolean
  nonCancellationReason?: string
  commissionAmount?: number
  multipleDaysCancelPolicyOption?: {
    cancelPolicies?: OrderCancelPolicyDomestic['cancelPolicies']
    checkInAt: string
    checkOutAt: string
    currentAt: string
  }
}

const colors = {
  FREE: theme.color.yagreen,
  EXCEPTIONAL_FREE: theme.color.yagreen,
  RATIO: theme.color.yaorange,
  FIXED: theme.color.yaorange,
  TOTAL: theme.color.yared,
  COMPLETED: theme.color.yared,
}

const CancelStatusText = styled.span`
  ${Typography('body2').bold};
  text-align: right;
`

const CancelPolicyTextWrapper = styled.div<{ lineBreak: boolean; color: string }>`
  margin-top: 0.4rem;
  display: grid;
  grid-auto-flow: ${({ lineBreak }) => (lineBreak ? 'row' : 'column')};
  ${({ lineBreak }) => (lineBreak ? '' : 'gap: 0.2rem')};
  justify-content: right;
  color: ${({ color }) => color};
  cursor: text;
`

const WarningNotice = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.color.yaorange};
  svg {
    flex: 0 0 1.6rem;
  }
  span {
    word-break: keep-all;
    ${Typography('body2').bold};
    margin-left: 0.2rem;
    margin-top: 0.1rem;
  }
`

const ExceptionFreeWrapper = styled.div`
  display: flex;
  align-items: center;

  @media (hover: hover) and (pointer: fine) {
    cursor: pointer;
  }
`

const OrangeHelpIcon = styled(Icon_help)`
  margin-left: 0.2rem;
  margin-top: -0.1rem;
  vertical-align: top;

  path {
    fill: ${({ theme }) => theme.color.yaorange};
  }
`

const GreenArrowIcon = styled(Icon_arrowforward_fill)`
  margin-top: 0.1rem;
  margin-left: 0.2rem;

  path {
    fill: ${({ theme }) => theme.color.yagreen};
  }
`
const ErrorIcon = styled(Icon_error)`
  path {
    fill: ${({ theme }) => theme.color.yaorange};
  }
`

// storybook 에서 export const 면 에러발생..
export function CancelPolicyFetchFailText({ className }: { className?: string }) {
  return (
    <WarningNotice className={className}>
      <ErrorIcon size={1.6} unit="rem" />
      <span>취소규정 정보를 불러오지 못했어요.</span>
    </WarningNotice>
  )
}

/**
 *  @description  국내숙소, 기차, 버스 취소규정 텍스트 컴포넌트
 *                레저는 다른 정책과 응답 필드를 사용하기에 별도 컴포넌트로 분리
 * */
const CancelPolicyText: React.FC<CancelPolicyProps> = ({
  cancelPolicySummary,
  setIsCancelFree,
  propertyCategory,
  isPendingReservation = false,
}) => {
  const [isOpened, setIsOpened] = useModalHash({ id: `exceptional-free` })

  const { type, statusText, showDate, cancelValidDate, lineBreak, showTooltip, isExceptionalFree } =
    useMemo(() => getCancelPolicyTextInfo(cancelPolicySummary), [cancelPolicySummary])

  useEffect(() => {
    if (setIsCancelFree) {
      setIsCancelFree(type === CancelPolicyType.FREE || type === CancelPolicyType.EXCEPTIONAL_FREE)
    }
  }, [type, setIsCancelFree])

  /**
   * 해당 컴포넌트에서 아무것도 노출되지 않으면 공백으로 인한
   * 불필요 margin-top (CancellationFeeWrapper) 이 생겨 null return
   */
  if (!statusText || !type || isPendingReservation) {
    return null
  }

  const {
    cancelPolicies,
    checkInAt = '',
    checkOutAt = '',
    currentAt = '',
  } = cancelPolicySummary.multipleDaysCancelPolicyOption ?? {}

  return (
    // React Portal 로 등록된 컴포넌트도 선언된 곳에서부터 이벤트 전이가 발생하기 때문에
    // 이를 막기 위해 stopPropagation 을 사용
    <div onClick={e => e.stopPropagation()}>
      <CancelPolicyTextWrapper lineBreak={lineBreak} color={colors[type]}>
        {showTooltip ? ( // 취소요청 화면 > 연박상품 && 취소 유형: Ratio
          <CancelPoliciesTooltip
            // showTooltip 이 true 이면 cancelPolicies 항상 truthy
            cancelPolicies={cancelPolicies!}
            checkInAt={checkInAt}
            checkOutAt={checkOutAt}
            currentAt={currentAt}
          >
            <CancelStatusText>{statusText}</CancelStatusText>
            <OrangeHelpIcon size={1.4} unit="rem" />
          </CancelPoliciesTooltip>
        ) : isExceptionalFree ? (
          <ExceptionFreeWrapper onClick={() => setIsOpened(true)}>
            <CancelStatusText>{statusText}</CancelStatusText>
            <GreenArrowIcon size={1.2} unit="rem" />
          </ExceptionFreeWrapper>
        ) : (
          <CancelStatusText>{statusText}</CancelStatusText>
        )}

        {showDate && cancelValidDate && (
          <CancelStatusText>({cancelValidDate}전까지)</CancelStatusText>
        )}
      </CancelPolicyTextWrapper>
      <ExceptionalFreeBottomSheet
        isOpened={isOpened}
        onRequestClose={() => setIsOpened(false)}
        propertyCategory={propertyCategory}
      />
    </div>
  )
}

export default CancelPolicyText
